import React, { useState, useEffect, useCallback } from 'react';
import {
  getBetaStatus,
  setLaunched,
  setPhase,
  setRewardsPaused,
  clearAdminOverrides,
  BETA_LAUNCH_CONFIG,
} from '../betaConfig';
import { getTGEStats, getAllUserData, downloadCSV, downloadJSON } from '../tgeExport';
import { resetEmissions } from '../utils/emissionSchedule';
import {
  getRecentActivity,
  getProtocolTotals,
  getProtocolBurnStats,
  getProtocolEmissionStats,
  getWalletStats,
  getAdminConfig,
  setAdminConfigValue,
  deleteAdminConfigValue,
} from '../utils/supabaseManager';
import { resetDailyCount, getDailyUsed, getDailyLimit } from '../dailyLimitsManager';
import { setBlockVisionApiKey, getBlockVisionApiKey, setBlockVisionRpcUrl, getBlockVisionRpcUrl } from '../utils/blockVisionApi';
import './AdminPanel.css';

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN PANEL - Only accessible by ADMIN_WALLET
// Full monitoring, controls, and TGE export dashboard
// ═══════════════════════════════════════════════════════════════════════════

function AdminPanel({ onClose }) {
  const [betaStatus, setBetaStatus] = useState(null);
  const [tgeStats, setTgeStats] = useState(null);
  const [emissionStats, setEmissionStats] = useState(null);
  const [protocolTotals, setProtocolTotals] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [burnStats, setBurnStats] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [confirmAction, setConfirmAction] = useState(null);
  const [bvApiKey, setBvApiKey] = useState('');
  const [bvApiKeySaved, setBvApiKeySaved] = useState(false);
  const [bvRpcUrl, setBvRpcUrl] = useState('');
  const [bvRpcUrlSaved, setBvRpcUrlSaved] = useState(false);

  // Admin Config tab state
  const [adminConfig, setAdminConfig] = useState({});
  const [configSaving, setConfigSaving] = useState({});
  const [configSaved, setConfigSaved] = useState({});
  const [configInputs, setConfigInputs] = useState({
    toilet_token_address: '',
    distil_token_address: '',
    burn_address: '',
  });

  // Per-wallet lookup state
  const [walletLookupAddress, setWalletLookupAddress] = useState('');
  const [walletLookupResult, setWalletLookupResult] = useState(null);
  const [walletLookupLoading, setWalletLookupLoading] = useState(false);
  const [walletLookupError, setWalletLookupError] = useState('');

  const loadData = useCallback(async () => {
    setBetaStatus(getBetaStatus());

    // Load existing BV API key
    const existingKey = getBlockVisionApiKey();
    if (existingKey) setBvApiKey(existingKey);
    const existingRpc = getBlockVisionRpcUrl();
    if (existingRpc) setBvRpcUrl(existingRpc);

    // Async from Supabase — burn + emission now come from Supabase too
    const [tge, totals, activity, burnData, emissionData, config] = await Promise.all([
      getTGEStats(),
      getProtocolTotals(),
      getRecentActivity(15),
      getProtocolBurnStats(),
      getProtocolEmissionStats(),
      getAdminConfig(),
    ]);
    setTgeStats(tge);
    setProtocolTotals(totals);
    setRecentActivity(activity);
    setBurnStats(burnData);
    setEmissionStats(emissionData);
    setAdminConfig(config);

    // Pre-fill config inputs from Supabase values
    setConfigInputs(prev => ({
      toilet_token_address: config.toilet_token_address || prev.toilet_token_address,
      distil_token_address: config.distil_token_address || prev.distil_token_address,
      burn_address: config.burn_address || prev.burn_address,
    }));

    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    if (autoRefresh) {
      const interval = setInterval(loadData, 30000);
      return () => clearInterval(interval);
    }
  }, [loadData, autoRefresh]);

  // ─── Admin controls ──────────────────────────────────────────────────
  const handleToggleLaunch = () => {
    const next = !betaStatus.isLaunched;
    setConfirmAction({
      label: next ? '🚀 LAUNCH BETA — this will start reward multipliers for all users. Are you sure?' : '⏸ PAUSE BETA — this will disable the launched state. Are you sure?',
      action: () => {
        setLaunched(next);
        setBetaStatus(getBetaStatus());
      }
    });
  };

  const handleToggleKillSwitch = () => {
    const next = !betaStatus.rewardsPaused;
    setConfirmAction({
      label: next
        ? '🛑 KILL SWITCH ON — ALL reward calculations will be paused immediately. Users will earn 0 $DISTIL until re-enabled. Confirm?'
        : '✅ RE-ENABLE REWARDS — resume normal reward calculations. Confirm?',
      action: () => {
        setRewardsPaused(next);
        setBetaStatus(getBetaStatus());
      }
    });
  };

  const handlePhaseChange = (phase) => {
    setPhase(phase);
    setBetaStatus(getBetaStatus());
  };

  const handleClearOverrides = () => {
    setConfirmAction({
      label: '🔄 CLEAR ALL OVERRIDES — revert to code defaults in constants.js. This will undo any admin changes made from this panel. Confirm?',
      action: () => {
        clearAdminOverrides();
        setBetaStatus(getBetaStatus());
      }
    });
  };

  const handleResetEmissions = () => {
    setConfirmAction({
      label: '⚠️ RESET EMISSION COUNTER — sets total emitted back to 0. Only do this in testing. Confirm?',
      action: () => {
        resetEmissions();
        // Note: Supabase emission stats will still reflect DB records
        // This only resets the local emissionSchedule counter
      }
    });
  };

  const handleSaveConfig = async (key) => {
    const value = configInputs[key]?.trim();
    if (!value) return;
    setConfigSaving(prev => ({ ...prev, [key]: true }));
    const ok = await setAdminConfigValue(key, value);
    setConfigSaving(prev => ({ ...prev, [key]: false }));
    if (ok) {
      setAdminConfig(prev => ({ ...prev, [key]: value }));
      setConfigSaved(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setConfigSaved(prev => ({ ...prev, [key]: false })), 3000);
    } else {
      alert('❌ Failed to save. Check Supabase connection.');
    }
  };

  const handleClearConfig = async (key) => {
    const ok = await deleteAdminConfigValue(key);
    if (ok) {
      setAdminConfig(prev => { const n = { ...prev }; delete n[key]; return n; });
      setConfigInputs(prev => ({ ...prev, [key]: '' }));
    }
  };

  const handleWalletLookup = async () => {
    const addr = walletLookupAddress.trim();
    if (!addr || addr.length < 10) {
      setWalletLookupError('Enter a valid wallet address');
      return;
    }
    setWalletLookupLoading(true);
    setWalletLookupError('');
    setWalletLookupResult(null);
    const result = await getWalletStats(addr);
    setWalletLookupLoading(false);
    if (!result) {
      setWalletLookupError('No data found for this address');
    } else {
      setWalletLookupResult(result);
    }
  };

  const handleClearAllStorage = () => {
    setConfirmAction({
      label: '🗑️ CLEAR ALL LOCALSTORAGE — wipes everything including your own stats, whitelist, and settings. Cannot be undone. Confirm?',
      action: () => {
        localStorage.clear();
        window.location.reload();
      }
    });
  };

  const handleResetDailyLimit = () => {
    resetDailyCount();
    alert(`Daily limit reset! Users can purify again. (Was: ${getDailyUsed()}/${getDailyLimit(false)} used)`);
  };

  const handleSaveRpcUrl = () => {
    if (!bvRpcUrl.trim()) return;
    setBlockVisionRpcUrl(bvRpcUrl.trim());
    localStorage.setItem('distil_bv_rpc_url', bvRpcUrl.trim());
    setBvRpcUrlSaved(true);
    setTimeout(() => setBvRpcUrlSaved(false), 3000);
  };

  const handleSaveBvApiKey = () => {
    if (!bvApiKey.trim()) return;
    setBlockVisionApiKey(bvApiKey.trim());
    localStorage.setItem('distil_bv_api_key', bvApiKey.trim());
    setBvApiKeySaved(true);
    setTimeout(() => setBvApiKeySaved(false), 3000);
  };

  const handleResetWheelCooldown = () => {
    localStorage.removeItem('distil_toilet_wheel');
    alert('🔄 Wheel cooldown reset!');
  };

  const handleResetScratchCard = () => {
    localStorage.removeItem('distil_scratch_data');
    alert('🎟️ Scratch card reset! Daily card restored.');
  };

  const handleResetFlushFrenzy = () => {
    localStorage.removeItem('distil_flush_data');
    alert('🎮 Flush Frenzy reset! Daily games restored.');
  };

  // ─── Helpers ─────────────────────────────────────────────────────────
  const fmt = (n) => (n || 0).toLocaleString();
  const formatAddress = (addr) => addr ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : 'N/A';
  const timeAgo = (ts) => {
    if (!ts) return 'Never';
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-loading">⏳ Loading admin panel...</div>
      </div>
    );
  }

  const isKillSwitchOn = betaStatus?.rewardsPaused;

  return (
    <div className="admin-panel">

      {/* ── CONFIRM MODAL ──────────────────────────────────────────────── */}
      {confirmAction && (
        <div className="admin-confirm-overlay" onClick={() => setConfirmAction(null)}>
          <div className="admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <p className="admin-confirm-text">{confirmAction.label}</p>
            <div className="admin-confirm-btns">
              <button className="admin-confirm-yes" onClick={() => { confirmAction.action(); setConfirmAction(null); }}>
                Yes, do it
              </button>
              <button className="admin-confirm-no" onClick={() => setConfirmAction(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div className="admin-header">
        <div>
          <h1 className="admin-title">🎯 Distil Admin</h1>
          <div className="admin-last-refresh">
            {lastRefresh ? `Last updated: ${timeAgo(lastRefresh)}` : 'Loading...'}
          </div>
        </div>
        <div className="admin-controls">
          <label className="auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (30s)
          </label>
          <button className="admin-btn" onClick={loadData}>🔄 Refresh</button>
          {onClose && (
            <button className="admin-btn" onClick={onClose} style={{ background: 'rgba(239,68,68,0.15)', borderColor: '#ef4444', color: '#ef4444' }}>
              ✖ Close
            </button>
          )}
        </div>
      </div>

      {/* ── KILL SWITCH BANNER (always visible if active) ──────────────── */}
      {isKillSwitchOn && (
        <div className="kill-switch-banner">
          🛑 KILL SWITCH ACTIVE — All rewards paused. Users are earning 0 $DISTIL.
          <button className="kill-switch-reenable" onClick={handleToggleKillSwitch}>
            ✅ Re-enable Rewards
          </button>
        </div>
      )}

      {/* ── BETA STATUS BANNER ─────────────────────────────────────────── */}
      <div className={`beta-status-banner ${betaStatus?.isLaunched ? 'live' : 'pre-launch'}`}>
        <div className="status-indicator">
          <span className="status-dot"></span>
          <span className="status-text">
            {betaStatus?.isLaunched
              ? `BETA LIVE — ${betaStatus.phase === 'private_beta' ? 'Private Beta' : 'Public Beta'}`
              : 'PRE-LAUNCH'}
          </span>
          {betaStatus?.hasOverrides && (
            <span className="override-badge">OVERRIDES ACTIVE</span>
          )}
        </div>
        <div className="status-details">
          <span>Multiplier: <strong>{isKillSwitchOn ? '0x 🛑' : `${betaStatus?.multiplier}x`}</strong></span>
          <span>Tracking: ON</span>
          {betaStatus?.launchDate && (
            <span>Since: {new Date(betaStatus.launchDate).toLocaleDateString()}</span>
          )}
        </div>
      </div>

      {/* ── TABS ───────────────────────────────────────────────────────── */}
      <div className="admin-tabs">
        {['overview', 'controls', 'config', 'activity', 'export'].map(tab => (
          <button
            key={tab}
            className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && '📊 Overview'}
            {tab === 'controls' && '🎛 Controls'}
            {tab === 'config' && '⚙️ Config'}
            {tab === 'activity' && '⚡ Activity'}
            {tab === 'export' && '📥 TGE Export'}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          TAB: OVERVIEW
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {/* Stats Grid — from Supabase */}
          <div className="admin-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{fmt(tgeStats?.totalUsers)}</div>
                <div className="stat-sub">{fmt(tgeStats?.toiletModeUsers)} Toilet Mode</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⚗️</div>
              <div className="stat-content">
                <div className="stat-label">Total Purifications</div>
                <div className="stat-value">{fmt(protocolTotals?.totalPurifications)}</div>
                <div className="stat-sub">{fmt(protocolTotals?.totalObjectsCleaned)} objects</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💎</div>
              <div className="stat-content">
                <div className="stat-label">Total $DISTIL Owed</div>
                <div className="stat-value">{fmt(tgeStats?.totalDistil)}</div>
                <div className="stat-sub">Avg {fmt(tgeStats?.averageDistil)} per user</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💧</div>
              <div className="stat-content">
                <div className="stat-label">SUI Reclaimed</div>
                <div className="stat-value">{(protocolTotals?.totalSuiReclaimed || 0).toFixed(4)}</div>
                <div className="stat-sub">across all users</div>
              </div>
            </div>
          </div>

          {/* Emission Stats */}
          <div className="admin-section">
            <h2 className="section-title">🔥 Emission Schedule <span style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 400 }}>— from Supabase</span></h2>
            <div className="emission-grid">
              <div className="emission-stat">
                <span className="emission-label">Total Emitted</span>
                <span className="emission-value">{fmt(emissionStats?.totalEmitted)} $DISTIL</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Rate</span>
                <span className="emission-value">{emissionStats?.currentRate} $DISTIL/object</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Phase</span>
                <span className="emission-value">{emissionStats?.currentPhase?.name || 'Launch Phase'}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Progress</span>
                <span className="emission-value">{(emissionStats?.percentageEmitted || 0).toFixed(3)}%</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Tokens to Next Phase</span>
                <span className="emission-value">{fmt(emissionStats?.tokensToNext)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Est. Days to Next Phase</span>
                <span className="emission-value">
                  {emissionStats?.estimatedDays ? `~${emissionStats.estimatedDays}d` : 'Not enough data'}
                </span>
              </div>
            </div>
          </div>

          {/* Burn Stats */}
          <div className="admin-section">
            <h2 className="section-title">♻️ Burn Tracker <span style={{ fontSize: '11px', color: '#22d3ee', fontWeight: 400 }}>— from Supabase</span></h2>
            <div className="emission-grid">
              <div className="emission-stat">
                <span className="emission-label">Total Burned</span>
                <span className="emission-value">{fmt(burnStats?.totalBurned)} $DISTIL</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Burn % of Supply</span>
                <span className="emission-value">{(burnStats?.burnPercentage || 0).toFixed(4)}%</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Weekly Burned</span>
                <span className="emission-value">{fmt(burnStats?.weeklyBurned)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Monthly Burned</span>
                <span className="emission-value">{fmt(burnStats?.monthlyBurned)}</span>
              </div>
            </div>
          </div>

          {/* Top User */}
          {tgeStats?.topUser && (
            <div className="admin-section">
              <h2 className="section-title">👑 Top User</h2>
              <div className="top-user-card">
                <div>
                  <div className="top-user-address">
                    {tgeStats.topUser.username || formatAddress(tgeStats.topUser.walletAddress)}
                  </div>
                  {tgeStats.topUser.username && (
                    <div style={{ color: '#64748b', fontSize: '12px', fontFamily: 'monospace' }}>
                      {formatAddress(tgeStats.topUser.walletAddress)}
                    </div>
                  )}
                </div>
                <div className="top-user-stats">
                  <span>{fmt(tgeStats.topUser.totalPurityEarned)} pts</span>
                  <span>{fmt(tgeStats.topUser.totalPurifications)} purifications</span>
                  <span>{fmt(tgeStats.topUser.distilAllocation)} $DISTIL</span>
                </div>
              </div>
            </div>
          )}

          {/* System Health */}
          <div className="admin-section">
            <h2 className="section-title">🔧 System Health</h2>
            <div className="health-grid">
              <HealthItem
                label="Supabase"
                ok={protocolTotals !== null}
                okText="Connected"
                warnText="Not responding"
              />
              <HealthItem
                label="Rewards"
                ok={!isKillSwitchOn}
                okText="Active"
                warnText="KILLED"
              />
              <HealthItem
                label="Beta State"
                ok={betaStatus?.isLaunched}
                okText="Live"
                warnText="Pre-launch"
              />
              <HealthItem
                label="Emission Tracker"
                ok={(emissionStats?.totalEmitted !== undefined)}
                okText="Tracking"
                warnText="No data"
              />
              <HealthItem
                label="Admin Overrides"
                ok={!betaStatus?.hasOverrides}
                okText="Using code defaults"
                warnText="Overrides active"
              />
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB: CONTROLS
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'controls' && (
        <>
          {/* TGE / Beta Launch */}
          <div className="admin-section">
            <h2 className="section-title">🚀 Beta Launch Control</h2>
            <p className="section-desc">
              Flipping this on activates reward multipliers for all users.
              Current code default: <code>{BETA_LAUNCH_CONFIG.isLaunched ? 'ON' : 'OFF'}</code>.
              Changes here override code without a redeploy.
            </p>
            <div className="control-row">
              <div className="control-info">
                <div className="control-label">Beta Status</div>
                <div className="control-current">
                  Currently: <strong style={{ color: betaStatus?.isLaunched ? '#22c55e' : '#fbbf24' }}>
                    {betaStatus?.isLaunched ? 'LIVE' : 'PRE-LAUNCH'}
                  </strong>
                </div>
              </div>
              <button
                className={`control-btn ${betaStatus?.isLaunched ? 'danger' : 'success'}`}
                onClick={handleToggleLaunch}
              >
                {betaStatus?.isLaunched ? '⏸ Pause Beta' : '🚀 Launch Beta'}
              </button>
            </div>

            <div className="control-row" style={{ marginTop: '1rem' }}>
              <div className="control-info">
                <div className="control-label">Beta Phase</div>
                <div className="control-current">
                  Currently: <strong>{betaStatus?.phase}</strong>
                </div>
              </div>
              <div className="phase-buttons">
                <button
                  className={`phase-btn ${betaStatus?.phase === 'private_beta' ? 'active' : ''}`}
                  onClick={() => handlePhaseChange('private_beta')}
                >
                  🔒 Private Beta (2x)
                </button>
                <button
                  className={`phase-btn ${betaStatus?.phase === 'public_beta' ? 'active' : ''}`}
                  onClick={() => handlePhaseChange('public_beta')}
                >
                  🌐 Public Beta (1.5x)
                </button>
              </div>
            </div>
          </div>

          {/* Kill Switch */}
          <div className="admin-section" style={{ borderColor: isKillSwitchOn ? 'rgba(239,68,68,0.5)' : undefined }}>
            <h2 className="section-title">🛑 Kill Switch</h2>
            <p className="section-desc">
              Instantly pauses ALL reward calculations. Use if a major bug or exploit is detected.
              Users can still purify but will earn 0 $DISTIL until re-enabled.
            </p>
            <div className="control-row">
              <div className="control-info">
                <div className="control-label">Reward Calculations</div>
                <div className="control-current">
                  Currently: <strong style={{ color: isKillSwitchOn ? '#ef4444' : '#22c55e' }}>
                    {isKillSwitchOn ? '🛑 PAUSED' : '✅ ACTIVE'}
                  </strong>
                </div>
              </div>
              <button
                className={`control-btn ${isKillSwitchOn ? 'success' : 'danger'}`}
                onClick={handleToggleKillSwitch}
              >
                {isKillSwitchOn ? '✅ Re-enable Rewards' : '🛑 Activate Kill Switch'}
              </button>
            </div>
          </div>

          {/* BlockVision API Key */}
          <div className="admin-section">
            <h2 className="section-title">🔑 BlockVision API Key</h2>
            <p className="section-desc">
              Used for instant, accurate USD price fetching. Rotate here without redeploying.
              Get your key at <strong>blockvision.org</strong> → My Apps → Distil Protocol.
            </p>
            <div className="debug-tools" style={{ flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="password"
                  value={bvApiKey}
                  onChange={e => setBvApiKey(e.target.value)}
                  placeholder="Paste BlockVision API key..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(34,211,238,0.3)',
                    background: 'rgba(0,0,0,0.3)',
                    color: '#e2e8f0',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                  }}
                />
                <button
                  className="debug-tool-btn"
                  onClick={handleSaveBvApiKey}
                  style={{ whiteSpace: 'nowrap', background: bvApiKeySaved ? 'rgba(34,197,94,0.2)' : undefined }}
                >
                  {bvApiKeySaved ? '✅ Saved!' : '💾 Save Key'}
                </button>
              </div>
              <p style={{ fontSize: '11px', color: 'rgba(148,163,184,0.7)', margin: 0 }}>
                Key is stored in localStorage and applied at runtime. Never committed to git.
              </p>
              <div style={{ borderTop: '1px solid rgba(34,211,238,0.15)', paddingTop: '12px', marginTop: '4px' }}>
                <p style={{ fontSize: '12px', color: 'rgba(148,163,184,0.9)', margin: '0 0 8px 0' }}>
                  <strong style={{ color: '#22d3ee' }}>Private RPC URL</strong> — find in BlockVision dashboard → My Apps → your app → RPC Endpoint. Rotate when you rotate your API key.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={bvRpcUrl}
                    onChange={e => setBvRpcUrl(e.target.value)}
                    placeholder="https://sui-mainnet.blockvision.org/v1/YOUR_KEY"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(34,211,238,0.3)',
                      background: 'rgba(0,0,0,0.3)',
                      color: '#e2e8f0',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                    }}
                  />
                  <button
                    className="debug-tool-btn"
                    onClick={handleSaveRpcUrl}
                    style={{ whiteSpace: 'nowrap', background: bvRpcUrlSaved ? 'rgba(34,197,94,0.2)' : undefined }}
                  >
                    {bvRpcUrlSaved ? '✅ Saved!' : '💾 Save RPC'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Override Management */}
          <div className="admin-section">
            <h2 className="section-title">🔄 Override Management</h2>
            <p className="section-desc">
              All controls above write to localStorage overrides — they survive page refreshes
              and take precedence over constants.js. Clearing overrides reverts to code defaults.
            </p>
            <div className="debug-tools">
              <button className="debug-tool-btn" onClick={handleClearOverrides}>
                🔄 Clear All Admin Overrides (revert to code defaults)
              </button>
              <button className="debug-tool-btn" onClick={handleResetWheelCooldown}>
                🎰 Reset Wheel Cooldown
              </button>
              <button className="debug-tool-btn" onClick={handleResetScratchCard}>
                🎟️ Reset Scratch Card (restore daily card)
              </button>
              <button className="debug-tool-btn" onClick={handleResetFlushFrenzy}>
                🎮 Reset Flush Frenzy (restore daily games)
              </button>
              <button className="debug-tool-btn" onClick={handleResetEmissions}>
                📉 Reset Emission Counter (testing only)
              </button>
              <button className="debug-tool-btn" onClick={handleResetDailyLimit}>
                📅 Reset Daily Purification Limit (testing)
              </button>
              <button className="debug-tool-btn warning" onClick={handleClearAllStorage}>
                🗑️ Clear ALL localStorage (nuclear option)
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB: CONFIG — Protocol addresses stored in Supabase
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'config' && (
        <>
          {/* Contract Addresses */}
          <div className="admin-section">
            <h2 className="section-title">⚙️ Protocol Config</h2>
            <p className="section-desc">
              These values are stored in Supabase and loaded by the app at runtime.
              No redeploy needed — save here and it's live instantly for all users.
            </p>

            {[
              {
                key: 'toilet_token_address',
                label: '🚽 $TOILET Token Contract Address',
                placeholder: '0x<package>::<module>::TOILET',
                hint: 'Paste the full Sui coin type for $TOILET. This unlocks Toilet Mode balance checks.',
              },
              {
                key: 'distil_token_address',
                label: '💎 $DISTIL Token Contract Address',
                placeholder: '0x<package>::distil::DISTIL',
                hint: 'Set after TGE. Used for holding bonus balance lookups.',
              },
              {
                key: 'burn_address',
                label: '🔥 Burn Address',
                placeholder: '0x0000000000000000000000000000000000000000000000000000000000000000',
                hint: 'The 64-char Sui zero address for permanent burns.',
              },
            ].map(({ key, label, placeholder, hint }) => (
              <div key={key} style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0', marginBottom: '4px' }}>
                  {label}
                </div>
                {adminConfig[key] && (
                  <div style={{
                    fontSize: '11px', fontFamily: 'monospace',
                    color: '#22d3ee', marginBottom: '6px',
                    background: 'rgba(34,211,238,0.07)', padding: '4px 8px', borderRadius: '4px',
                  }}>
                    ✅ Saved: {adminConfig[key]}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={configInputs[key] || ''}
                    onChange={e => setConfigInputs(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid rgba(34,211,238,0.3)',
                      background: 'rgba(0,0,0,0.3)', color: '#e2e8f0',
                      fontSize: '12px', fontFamily: 'monospace',
                    }}
                  />
                  <button
                    className="debug-tool-btn"
                    onClick={() => handleSaveConfig(key)}
                    disabled={configSaving[key]}
                    style={{
                      whiteSpace: 'nowrap',
                      background: configSaved[key] ? 'rgba(34,197,94,0.2)' : undefined,
                      opacity: configSaving[key] ? 0.6 : 1,
                    }}
                  >
                    {configSaving[key] ? '⏳' : configSaved[key] ? '✅ Saved!' : '💾 Save'}
                  </button>
                  {adminConfig[key] && (
                    <button
                      className="debug-tool-btn warning"
                      onClick={() => handleClearConfig(key)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🗑️ Clear
                    </button>
                  )}
                </div>
                <p style={{ fontSize: '11px', color: 'rgba(148,163,184,0.7)', margin: '4px 0 0 0' }}>
                  {hint}
                </p>
              </div>
            ))}
          </div>

          {/* Per-Wallet Lookup */}
          <div className="admin-section">
            <h2 className="section-title">🔍 Wallet Lookup</h2>
            <p className="section-desc">
              Enter any wallet address to see their full stats from Supabase.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                value={walletLookupAddress}
                onChange={e => setWalletLookupAddress(e.target.value)}
                placeholder="0x..."
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: '6px',
                  border: '1px solid rgba(34,211,238,0.3)',
                  background: 'rgba(0,0,0,0.3)', color: '#e2e8f0',
                  fontSize: '12px', fontFamily: 'monospace',
                }}
                onKeyDown={e => e.key === 'Enter' && handleWalletLookup()}
              />
              <button
                className="debug-tool-btn"
                onClick={handleWalletLookup}
                disabled={walletLookupLoading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {walletLookupLoading ? '⏳ Loading...' : '🔍 Look Up'}
              </button>
            </div>

            {walletLookupError && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{walletLookupError}</div>
            )}

            {walletLookupResult && (
              <div style={{
                background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.2)',
                borderRadius: '8px', padding: '14px',
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                  {walletLookupResult.walletAddress}
                </div>
                {walletLookupResult.username && (
                  <div style={{ fontSize: '14px', color: '#22d3ee', fontWeight: 600, marginBottom: '8px' }}>
                    👤 {walletLookupResult.username}
                  </div>
                )}
                <div className="emission-grid">
                  {[
                    ['💎 Purity Earned', fmt(walletLookupResult.totalPurityEarned) + ' pts'],
                    ['⚗️ Purifications', fmt(walletLookupResult.totalPurifications)],
                    ['🗑️ Objects Cleaned', fmt(walletLookupResult.totalObjectsCleaned)],
                    ['💧 SUI Reclaimed', walletLookupResult.totalSuiReclaimed.toFixed(4)],
                    ['🚽 Toilet Purges', fmt(walletLookupResult.totalToiletPurifications)],
                    ['🎮 Game Rewards', fmt(walletLookupResult.gameRewardTotal) + ' pts'],
                    ['⏱️ Last Active', walletLookupResult.lastPurification ? timeAgo(walletLookupResult.lastPurification) : 'Never'],
                  ].map(([label, value]) => (
                    <div key={label} className="emission-stat">
                      <span className="emission-label">{label}</span>
                      <span className="emission-value">{value}</span>
                    </div>
                  ))}
                </div>
                {Object.keys(walletLookupResult.gameTypeCounts).length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#94a3b8' }}>
                    <strong style={{ color: '#a855f7' }}>Game breakdown: </strong>
                    {Object.entries(walletLookupResult.gameTypeCounts).map(([type, count]) => (
                      <span key={type} style={{ marginRight: '12px' }}>
                        {type.replace('_', ' ')}: {count}x
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB: ACTIVITY
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'activity' && (
        <div className="admin-section">
          <h2 className="section-title">⚡ Recent Activity (Supabase — Live)</h2>
          <div className="activity-list">
            {recentActivity.length === 0 ? (
              <div className="activity-empty">No purifications recorded yet in Supabase.</div>
            ) : (
              recentActivity.map((activity, index) => {
                const gameLabel = {
                  flush_frenzy: '🎮 Flush Frenzy',
                  scratch_off: '🎟️ Scratch Off',
                  spin_wheel: '🎰 Spin Wheel',
                }[activity.gameType] || null;

                return (
                  <div key={index} className="activity-item">
                    <div className="activity-user">
                      {activity.address}
                      {activity.isToiletMode && <span className="toilet-badge-chip" style={{ marginLeft: 6 }}>🚽</span>}
                      {gameLabel && (
                        <span style={{
                          marginLeft: 6,
                          fontSize: '11px',
                          background: 'rgba(168,85,247,0.2)',
                          color: '#a855f7',
                          borderRadius: '4px',
                          padding: '1px 5px',
                        }}>{gameLabel}</span>
                      )}
                    </div>
                    <div className="activity-details">
                      <span>+{activity.purityEarned} pts</span>
                      {!gameLabel && <span>{activity.objectCount} objects</span>}
                      {activity.gameScore && <span>score: {activity.gameScore}</span>}
                      {activity.suiReclaimed > 0 && (
                        <span>{activity.suiReclaimed.toFixed(4)} SUI</span>
                      )}
                      {activity.transactionHash && (
                        <a
                          href={`https://suiscan.xyz/mainnet/tx/${activity.transactionHash}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#22d3ee', fontSize: '11px' }}
                        >
                          ↗ tx
                        </a>
                      )}
                    </div>
                    <div className="activity-time">{timeAgo(activity.timestamp)}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB: TGE EXPORT
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'export' && (
        <>
          <div className="admin-section">
            <h2 className="section-title">📊 TGE Snapshot Summary</h2>
            <div className="emission-grid">
              <div className="emission-stat">
                <span className="emission-label">Total Users</span>
                <span className="emission-value">{fmt(tgeStats?.totalUsers)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Total $DISTIL to Distribute</span>
                <span className="emission-value">{fmt(tgeStats?.totalDistil)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Average Per User</span>
                <span className="emission-value">{fmt(tgeStats?.averageDistil)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Toilet Mode Users</span>
                <span className="emission-value">{fmt(tgeStats?.toiletModeUsers)}</span>
              </div>
              <div className="emission-stat">
                <span className="emission-label">Snapshot Time</span>
                <span className="emission-value" style={{ fontSize: '12px' }}>
                  {tgeStats?.snapshotTime ? new Date(tgeStats.snapshotTime).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-section">
            <h2 className="section-title">📥 Export Data</h2>
            <p className="section-desc">
              Exports pull directly from Supabase in real time — always current.
              CSV is for spreadsheets. JSON includes full metadata for airdrop tooling.
            </p>
            <div className="export-buttons">
              <button className="export-btn" onClick={downloadCSV}>
                📄 Download CSV
              </button>
              <button className="export-btn" onClick={downloadJSON}>
                📋 Download JSON
              </button>
              <button
                className="export-btn primary"
                onClick={async () => {
                  const stats = await getTGEStats();
                  console.table(stats);
                  alert(`TGE Stats logged to console!\nUsers: ${stats.totalUsers}\n$DISTIL owed: ${fmt(stats.totalDistil)}`);
                }}
              >
                📊 Log to Console
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

// ─── Health item component ─────────────────────────────────────────────────
function HealthItem({ label, ok, okText, warnText }) {
  return (
    <div className={`health-item ${ok ? 'ok' : 'warning'}`}>
      <span className="health-icon">{ok ? '✅' : '⚠️'}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: '13px' }}>{label}</div>
        <div style={{ fontSize: '11px', opacity: 0.8 }}>{ok ? okText : warnText}</div>
      </div>
    </div>
  );
}

export default AdminPanel;
